import React, { useState, useEffect, useRef, useCallback } from "react";

import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Chip,
  Divider,
  List,
  ListItem,
  ListItemText,
  CircularProgress,
  Container,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Paper,
  Button,
  Badge,
} from "@mui/material";

import {
  ExpandMore as ExpandMoreIcon,
  Speed as SpeedIcon,
  StopCircle as StopCircleIcon,
  Warning as WarningIcon,
  Analytics as AnalyticsIcon,
  Close as CloseIcon,
  LocationOn as LocationOnIcon,
  TrendingUp as TrendingUpIcon,
  CheckCircle as CheckCircleIcon,
  DirectionsCar as DirectionsCarIcon,
} from "@mui/icons-material";

import {
  PieChart,
  Pie,
  Cell,
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";

import { useParams, useLocation } from "react-router-dom";
import config from "../../Data/AuthUser";
import { TripsApi, DriverApis } from "../../Data/axios";
import "./TripDetails.css";

const formatDateTime = (dateString) => {
  if (!dateString) return "N/A";
  const date = new Date(dateString);
  if (isNaN(date)) return "N/A";
  const dd = String(date.getDate()).padStart(2, "0");
  const mm = String(date.getMonth() + 1).padStart(2, "0");
  const yyyy = date.getFullYear();
  const hh = String(date.getHours()).padStart(2, "0");
  const min = String(date.getMinutes()).padStart(2, "0");
  return `${dd}-${mm}-${yyyy} ${hh}:${min}`;
};

const formatDuration = (seconds) => {
  if (!seconds && seconds !== 0) return "0s";
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;
  return hours > 0 ? `${hours}h ${minutes}m` : `${minutes}m ${secs}s`;
};

const TripDetails = () => {
  const { id } = useParams();
  const location = useLocation();
  const driverIdFromState = location.state?.driver_id;

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingStage, setLoadingStage] = useState('fetching'); // fetching, analyzing, mapping, processing, rendering
  const [error, setError] = useState(null);
  const [liveStatus, setLiveStatus] = useState(null);
  const [driverImageError, setDriverImageError] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [dailyStats, setDailyStats] = useState(null);
  const [dailyStatsLoading, setDailyStatsLoading] = useState(false);
  const [stopsPage, setStopsPage] = useState(0);
  const [stopsSortBy, setStopsSortBy] = useState("time");
  const [stopsSortOrder, setStopsSortOrder] = useState("asc");
  const [addresses, setAddresses] = useState({});
  const [selectedStopIdx, setSelectedStopIdx] = useState(null);
  const [expandedAccordion, setExpandedAccordion] = useState('stops');
  const [fastagData, setFastagData] = useState(null);
  const [loadingFastag, setLoadingFastag] = useState(false);
  const [fastagPage, setFastagPage] = useState(0);
  const fastagRowsPerPage = 15;
  const mapRef = useRef(null);
  const mapContainerRef = useRef(null);
  const stopsRowsPerPage = 15;

  // Reverse geocode a lat/lon to an address using Nominatim
  const reverseGeocode = useCallback(async (lat, lon) => {
    const key = `${lat.toFixed(4)},${lon.toFixed(4)}`;
    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lon}&zoom=16&addressdetails=1`,
        { headers: { "Accept-Language": "en" } }
      );
      const data = await response.json();
      const address = data?.display_name || `${lat.toFixed(4)}, ${lon.toFixed(4)}`;
      setAddresses(prev => ({ ...prev, [key]: address }));
    } catch (err) {
      setAddresses(prev => ({ ...prev, [key]: `${lat.toFixed(4)}, ${lon.toFixed(4)}` }));
    }
  }, []);

  // Get cached address or return loading text
  const getAddress = useCallback((lat, lon) => {
    const key = `${lat.toFixed(4)},${lon.toFixed(4)}`;
    return addresses[key] || "Loading address...";
  }, [addresses]);

  // Parse geocode coordinates from string "lat,lon"
  const parseGeocode = useCallback((geocodeStr) => {
    if (!geocodeStr) return { lat: 0, lon: 0 };
    try {
      const [latStr, lonStr] = geocodeStr.split(',').map(s => s.trim());
      return {
        lat: parseFloat(latStr),
        lon: parseFloat(lonStr)
      };
    } catch {
      return { lat: 0, lon: 0 };
    }
  }, []);

  // Fetch Fasttag data from API
  useEffect(() => {
    const fetchFastagData = async () => {
      if (!id) return;

      try {
        setLoadingFastag(true);
        const response = await fetch(`${config.apiBaseUrl}/trips/${id}/fastag-data`);
        const result = await response.json();

        if (result.status && result.data) {
          setFastagData(result.data);

          // Geocode all fastag transaction locations
          result.data.fastag_transactions?.forEach((transaction, idx) => {
            const { lat, lon } = parseGeocode(transaction.toll_plaza_geocode);
            if (lat && lon) {
              setTimeout(() => {
                reverseGeocode(lat, lon);
              }, idx * 1100); // Rate-limited geocoding
            }
          });
        }
      } catch (err) {
        console.error('Error fetching fastag data:', err);
      } finally {
        setLoadingFastag(false);
      }
    };

    fetchFastagData();
  }, [id, parseGeocode, reverseGeocode]);

  // Handle clicking a stop row - zoom map to that location
  const handleStopClick = useCallback((stop, idx) => {
    setSelectedStopIdx(idx);
    if (mapRef.current && window.L) {
      const map = mapRef.current;
      map.setView([stop.lat, stop.lon], 16, { animate: true });
      const popup = window.L.popup()
        .setLatLng([stop.lat, stop.lon])
        .setContent(`
          <div style="font-size: 12px; color: #000; font-family: Arial;">
            <strong>Stop #${idx + 1}</strong><br/>
            Time: ${formatDateTime(stop.start_time)}<br/>
            Duration: ${formatDuration(stop.duration_sec)}<br/>
            ${getAddress(stop.lat, stop.lon)}
          </div>
        `)
        .openOn(map);
    }
    // Scroll to map
    const mapEl = document.getElementById("trip-map");
    if (mapEl) mapEl.scrollIntoView({ behavior: "smooth", block: "center" });
  }, [getAddress]);

  useEffect(() => {
    const fetchTripAnalysis = async () => {
      try {
        setLoading(true);
        setLoadingStage('fetching');

        const applicant_id = driverIdFromState || config.applicant_id || config.user_id;

        if (!id) {
          setError("Trip ID is missing from URL.");
          return;
        }

        // Simulate initial data fetching delay for better UX
        await new Promise(resolve => setTimeout(resolve, 800));

        // Get local system timezone
        const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

        setLoadingStage('analyzing');

        const res = await TripsApi.getPerformanceAnalysis(applicant_id, id, timezone);

        if (res.success) {
          setData(res.data);

          // Simulate analysis processing time
          await new Promise(resolve => setTimeout(resolve, 1200));

          setLoadingStage('mapping');

          // Simulate mapping preparation time
          await new Promise(resolve => setTimeout(resolve, 1000));

          setLoadingStage('processing');

          // Simulate location processing time
          await new Promise(resolve => setTimeout(resolve, 800));

          setLoadingStage('rendering');

          // Simulate final rendering preparation
          await new Promise(resolve => setTimeout(resolve, 500));
        } else {
          setError(res.message);
        }
      } catch (err) {
        setError("Failed to connect to the server.");
      } finally {
        setLoading(false);
      }
    };

    fetchTripAnalysis();
  }, [id, driverIdFromState]);

  // Fetch daily stats using the new API
  useEffect(() => {
    const fetchDailyStats = async () => {
      try {
        setDailyStatsLoading(true);
        const applicant_id = driverIdFromState || config.applicant_id || config.user_id;

        if (!id) {
          console.error("Trip ID is missing from URL.");
          return;
        }

        // Get local system timezone
        const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

        const res = await TripsApi.getDailyStats(applicant_id, id, timezone);

        if (res.success) {
          setDailyStats(res.data?.daily_stats || []);
        } else {
          console.error("Failed to fetch daily stats:", res.message);
        }
      } catch (err) {
        console.error("Error fetching daily stats:", err);
      } finally {
        setDailyStatsLoading(false);
      }
    };

    fetchDailyStats();
  }, [id, driverIdFromState]);

  // Fetch live driver status scoped to this exact trip
  useEffect(() => {
    const fetchLiveStatus = async () => {
      if (!id) return;
      const userIds = driverIdFromState ? [driverIdFromState] : [];
      const data = await DriverApis.getLiveStatus({ tripIds: [id], userIds });
      const byTrip = data[id];
      if (byTrip) {
        setLiveStatus(byTrip.live_status || null);
        return;
      }
      // Older backend keys by user_id — accept only if it belongs to this exact trip
      const match = Object.values(data).find(
        (e) => e?.trip_id != null && String(e.trip_id) === String(id)
      );
      if (match) {
        setLiveStatus(match.live_status || null);
        return;
      }
      // Legacy backend: no trip_id in payload — fall back to the driver's entry keyed by user_id
      const legacy = driverIdFromState ? data[driverIdFromState] : null;
      setLiveStatus(legacy?.live_status || null);
    };

    fetchLiveStatus();
    const timer = setInterval(fetchLiveStatus, 30000);
    return () => clearInterval(timer);
  }, [id, driverIdFromState]);

  const getLiveBadge = (status) => {
    switch (status) {
      case "moving":
        return { label: "Moving", color: "#2e7d32", bg: "#e8f5e9" };
      case "offline":
        return { label: "Offline", color: "#ed6c02", bg: "#fff3e0" };
      case "rest":
        return { label: "Rest", color: "#d32f2f", bg: "#fdecea" };
      default:
        return null;
    }
  };

  const getInitials = (name) => {
    if (!name) return "NA";
    const words = name.trim().split(" ");
    if (words.length === 1) return words[0].charAt(0).toUpperCase();
    return (words[0].charAt(0) + words[words.length - 1].charAt(0)).toUpperCase();
  };

  // FIX: Destructure data HERE so variables are available to subsequent hooks
  const analysis = data?.analysis || {};
  const trip_records = data?.trip_records || [];
  const driver = data?.driver || {};
  const { summary = {}, daily_stats = [], stops = [], high_speed_events = [] } = analysis;
  const trip_status = data?.trip_status;

  useEffect(() => {
    setDriverImageError(false);
  }, [driver?.image]);

  // Initialize Leaflet map with markers
  useEffect(() => {
    if (!data || !stops || stops.length === 0) return;

    // Dynamically load Leaflet CSS
    const leafletCSS = document.createElement("link");
    leafletCSS.rel = "stylesheet";
    leafletCSS.href = "https://unpkg.com/leaflet@1.9.4/dist/leaflet.css";
    document.head.appendChild(leafletCSS);

    // Dynamically load Leaflet JS
    const leafletScript = document.createElement("script");
    leafletScript.src = "https://unpkg.com/leaflet@1.9.4/dist/leaflet.js";
    leafletScript.onload = () => {
      const L = window.L;
      const mapContainer = document.getElementById("trip-map");
      if (!mapContainer || !L) return;

      // Create map
      const centerLat = (Math.max(...stops.map(s => s.lat)) + Math.min(...stops.map(s => s.lat))) / 2;
      const centerLon = (Math.max(...stops.map(s => s.lon)) + Math.min(...stops.map(s => s.lon))) / 2;

      const map = L.map(mapContainer).setView([centerLat, centerLon], 10);
      mapRef.current = map;

      // Add base layer
      L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
        attribution: "© OpenStreetMap contributors",
        maxZoom: 19,
      }).addTo(map);

      // Add stop markers (orange)
      stops?.forEach((stop, idx) => {
        const marker = L.circleMarker([stop.lat, stop.lon], {
          radius: 6,
          fillColor: "#ff9800",
          color: "#f57c00",
          weight: 2,
          opacity: 1,
          fillOpacity: 0.8,
        }).addTo(map);

        const popupContent = `
          <div style="font-size: 12px; color: #000; font-family: Arial;">
            <strong>Stop #${idx + 1}</strong><br/>
            Time: ${formatDateTime(stop.start_time)}<br/>
            Duration: ${formatDuration(stop.duration_sec)}<br/>
            Lat: ${stop.lat.toFixed(4)}, Lon: ${stop.lon.toFixed(4)}
          </div>
        `;

        marker.bindPopup(popupContent);
        marker.on("mouseover", function () {
          this.openPopup();
        });
        marker.on("mouseout", function () {
          this.closePopup();
        });
      });

      // Add high speed events markers (red)
      high_speed_events?.forEach((event, idx) => {
        if (event.lat && event.lon) {
          const marker = L.circleMarker([event.lat, event.lon], {
            radius: 6,
            fillColor: "#f44336",
            color: "#d32f2f",
            weight: 2,
            opacity: 1,
            fillOpacity: 0.8,
          }).addTo(map);

          const popupContent = `
            <div style="font-size: 12px; color: #000; font-family: Arial;">
              <strong>High Speed Event #${idx + 1}</strong><br/>
              Time: ${formatDateTime(event.start_time)}<br/>
              Peak Speed: ${event.peak_speed?.toFixed(1)} km/h<br/>
              Duration: ${formatDuration(event.duration_sec)}
            </div>
          `;

          marker.bindPopup(popupContent);
          marker.on("mouseover", function () {
            this.openPopup();
          });
          marker.on("mouseout", function () {
            this.closePopup();
          });
        }
      });

      // Add trip logs markers (blue)
      trip_records?.forEach((record, idx) => {
        if (record.attachments?.[0]?.lat && record.attachments?.[0]?.lon) {
          const marker = L.circleMarker([record.attachments[0].lat, record.attachments[0].lon], {
            radius: 6,
            fillColor: "#2196f3",
            color: "#1976d2",
            weight: 2,
            opacity: 1,
            fillOpacity: 0.8,
          }).addTo(map);

          const popupContent = `
            <div style="font-size: 12px; color: #000; font-family: Arial; max-width: 250px;">
              <strong>Log #${idx + 1}</strong><br/>
              Content: ${record.text_content || "Location Broadcast"}<br/>
              Time: ${formatDateTime(record.created_at)}<br/>
              Type: ${record.attachments?.[0]?.type || "Unknown"}
            </div>
          `;

          marker.bindPopup(popupContent);
          marker.on("mouseover", function () {
            this.openPopup();
          });
          marker.on("mouseout", function () {
            this.closePopup();
          });
        }
      });

      // Fit map to bounds
      const bounds = L.latLngBounds(stops.map(s => [s.lat, s.lon]));
      map.fitBounds(bounds.pad(0.1));
    };

    document.body.appendChild(leafletScript);

    return () => {
      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null;
      }
      if (leafletScript.parentNode) {
        leafletScript.parentNode.removeChild(leafletScript);
      }
    };
  }, [data, stops, high_speed_events, trip_records]);

  // Batch reverse-geocode all stop and high-speed event locations
  useEffect(() => {
    if (!stops || stops.length === 0) return;

    const allCoords = [];
    stops.forEach(s => {
      const key = `${s.lat.toFixed(4)},${s.lon.toFixed(4)}`;
      if (!addresses[key]) allCoords.push({ lat: s.lat, lon: s.lon });
    });
    high_speed_events?.forEach(e => {
      if (e.lat && e.lon) {
        const key = `${e.lat.toFixed(4)},${e.lon.toFixed(4)}`;
        if (!addresses[key]) allCoords.push({ lat: e.lat, lon: e.lon });
      }
    });

    // Deduplicate
    const uniqueCoords = [];
    const seen = new Set();
    allCoords.forEach(c => {
      const key = `${c.lat.toFixed(4)},${c.lon.toFixed(4)}`;
      if (!seen.has(key)) {
        seen.add(key);
        uniqueCoords.push(c);
      }
    });

    // Rate-limited geocoding (1 request per second for Nominatim)
    uniqueCoords.forEach((coord, idx) => {
      setTimeout(() => {
        reverseGeocode(coord.lat, coord.lon);
      }, idx * 1100); // 1.1s between requests
    });
  }, [stops, high_speed_events]); // eslint-disable-line react-hooks/exhaustive-deps

  if (loading)
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "80vh",
          backgroundColor: "#f5f5f5",
        }}
      >
        <CircularProgress />
      </Box>
    );

  if (error)
    return (
      <Container sx={{ py: 4, backgroundColor: "#f5f5f5" }}>
        <Alert severity="error">{error}</Alert>
      </Container>
    );

  const hasData = summary && Object.keys(summary).length > 0;

  if (!hasData)
    return (
      <Container sx={{ py: 4, backgroundColor: "#f5f5f5" }}>
        <Typography variant="h5" fontWeight={700} gutterBottom>
          Trip Analysis
        </Typography>
        <Alert severity="info">No performance analysis data available for this trip.</Alert>
      </Container>
    );

  // Calculate derived metrics
  const totalStopTime = stops?.reduce((sum, stop) => sum + stop.duration_sec, 0) || 0;
  const drivingTimeSeconds = (summary.distance_covered_km / (summary.average_speed_kmh || 1)) * 3600;
  const safetyScore = Math.max(0, 100 - ((summary.dangerous_speeds_count || 0) / Math.max(high_speed_events?.length || 1, 1)) * 100);
  const efficiency = summary.distance_covered_km / Math.max((drivingTimeSeconds / 3600), 1);

  // Calculate performance ratings
  const avgDistancePerDay = summary.average_distance_per_day_km || 0;
  let distanceRating = "Poor";
  if (avgDistancePerDay > 475) distanceRating = "Extreme";
  else if (avgDistancePerDay >= 300) distanceRating = "Good";

  const avgSpeedRating = summary.average_speed_kmh > 80 ? "High" : summary.average_speed_kmh > 50 ? "Moderate" : "Low";

  const stopsRating = summary.total_stops > 20 ? "Many" : summary.total_stops > 10 ? "Moderate" : "Few";

  const safetyRating = safetyScore > 90 ? "Excellent" : safetyScore > 75 ? "Good" : safetyScore > 50 ? "Fair" : "Poor";

  // Sorting function
  const sortStops = () => {
    let sorted = [...(stops || [])];
    sorted.sort((a, b) => {
      let aVal, bVal;
      if (stopsSortBy === "time") {
        aVal = new Date(a.start_time).getTime();
        bVal = new Date(b.start_time).getTime();
      } else if (stopsSortBy === "duration") {
        aVal = a.duration_sec;
        bVal = b.duration_sec;
      }
      return stopsSortOrder === "asc" ? aVal - bVal : bVal - aVal;
    });
    return sorted;
  };

  const handleStopsSort = (field) => {
    if (stopsSortBy === field) {
      setStopsSortOrder(stopsSortOrder === "asc" ? "desc" : "asc");
    } else {
      setStopsSortBy(field);
      setStopsSortOrder("asc");
    }
  };

  const sortedStops = sortStops();

  // Prepare speed distribution data
  const speedDistribution = [
    {
      name: "80-90 km/h",
      value: high_speed_events?.filter(e => e.peak_speed >= 80 && e.peak_speed < 90).length || 0,
      color: "#4CAF50",
    },
    {
      name: "90-100 km/h",
      value: high_speed_events?.filter(e => e.peak_speed >= 90 && e.peak_speed < 100).length || 0,
      color: "#FF9800",
    },
    {
      name: "100-110 km/h",
      value: high_speed_events?.filter(e => e.peak_speed >= 100 && e.peak_speed < 110).length || 0,
      color: "#FF7043",
    },
    {
      name: "110+ km/h",
      value: high_speed_events?.filter(e => e.peak_speed >= 110).length || 0,
      color: "#F44336",
    },
  ];

  // Prepare speed trends data
  const speedTrends = high_speed_events
    ?.slice(0, 20)
    .map((event, idx) => ({
      time: `${Math.round((idx / 20) * 100)}%`,
      avgSpeed: summary.average_speed_kmh || 0,
      peakSpeed: event.peak_speed,
    })) || [];

  // Prepare longest stops data
  const longestStops = stops
    ?.map((stop, idx) => ({
      name: `Stop ${idx + 1}`,
      duration: Math.round(stop.duration_sec / 60),
    }))
    .sort((a, b) => b.duration - a.duration)
    .slice(0, 10) || [];

  // Multi-stage loading component
  const LoadingStage = () => {
    const stages = [
      { key: 'fetching', label: 'Fetching Trip Data', icon: '📡', progress: 20 },
      { key: 'analyzing', label: 'Analyzing Trip Performance', icon: '📊', progress: 40 },
      { key: 'mapping', label: 'Mapping Route Details', icon: '🗺️', progress: 60 },
      { key: 'processing', label: 'Processing Locations', icon: '📍', progress: 80 },
      { key: 'rendering', label: 'Rendering Trip Dashboard', icon: '🎨', progress: 95 },
    ];

    const currentStageIndex = stages.findIndex(stage => stage.key === loadingStage);

    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          backgroundColor: '#f5f5f5',
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 9999,
        }}
      >
        {/* Main Loading Animation */}
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Box sx={{ position: 'relative', display: 'inline-block' }}>
            {/* Outer rotating ring */}
            <Box
              className="loading-spinner"
              sx={{
                width: 120,
                height: 120,
                borderRadius: '50%',
                border: '4px solid #e3f2fd',
                borderTop: '4px solid #1976d2',
                position: 'absolute',
              }}
            />
            {/* Middle rotating ring */}
            <Box
              className="loading-spinner-reverse"
              sx={{
                width: 90,
                height: 90,
                borderRadius: '50%',
                border: '3px solid #fff3e0',
                borderTop: '3px solid #f57c00',
                position: 'absolute',
                top: 15,
                left: 15,
              }}
            />
            {/* Inner pulsing circle */}
            <Box
              className="loading-pulse"
              sx={{
                width: 60,
                height: 60,
                borderRadius: '50%',
                background: 'linear-gradient(135deg, #1976d2 0%, #f57c00 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                position: 'absolute',
                top: 30,
                left: 30,
              }}
            >
              <Typography variant="h4" sx={{ color: 'white', fontWeight: 'bold' }}>
                {stages[currentStageIndex]?.icon || '📡'}
              </Typography>
            </Box>
          </Box>

          {/* Current Stage Text */}
          <Typography
            className="loading-fade-in-up"
            variant="h5"
            sx={{
              mt: 8,
              fontWeight: 600,
              color: '#1976d2',
            }}
          >
            {stages[currentStageIndex]?.label || 'Loading...'}
          </Typography>

          {/* Progress Bar */}
          <Box sx={{ width: 300, mt: 3, mx: 'auto' }}>
            <Box
              sx={{
                height: 8,
                borderRadius: 4,
                backgroundColor: '#e0e0e0',
                overflow: 'hidden',
              }}
            >
              <Box
                className="loading-shimmer"
                sx={{
                  height: '100%',
                  width: `${stages[currentStageIndex]?.progress || 0}%`,
                  background: 'linear-gradient(90deg, #1976d2 0%, #f57c00 100%)',
                  borderRadius: 4,
                  transition: 'width 0.5s ease-in-out',
                }}
              />
            </Box>
            <Typography
              variant="body2"
              sx={{ mt: 1, color: '#666', fontWeight: 500 }}
            >
              {stages[currentStageIndex]?.progress || 0}% Complete
            </Typography>
          </Box>
        </Box>

        {/* Stage Indicators */}
        <Box
          sx={{
            display: 'flex',
            gap: 2,
            mt: 4,
            flexWrap: 'wrap',
            justifyContent: 'center',
            maxWidth: 600,
          }}
        >
          {stages.map((stage, index) => (
            <Box
              key={stage.key}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                px: 2,
                py: 1,
                borderRadius: 2,
                backgroundColor: index <= currentStageIndex ? '#e3f2fd' : '#f5f5f5',
                border: index <= currentStageIndex ? '2px solid #1976d2' : '2px solid #e0e0e0',
                opacity: index <= currentStageIndex ? 1 : 0.5,
                transition: 'all 0.3s ease',
              }}
            >
              <Typography sx={{ fontSize: '1.2rem' }}>
                {stage.icon}
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  fontWeight: index <= currentStageIndex ? 600 : 400,
                  color: index <= currentStageIndex ? '#1976d2' : '#666',
                  fontSize: '0.75rem',
                }}
              >
                {stage.label}
              </Typography>
              {index < currentStageIndex && (
                <CheckCircleIcon sx={{ color: '#4caf50', fontSize: '1rem' }} />
              )}
            </Box>
          ))}
        </Box>

        {/* Loading Tip */}
        <Box
          sx={{
            mt: 4,
            p: 2,
            backgroundColor: '#fff',
            borderRadius: 2,
            maxWidth: 400,
            textAlign: 'center',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          }}
        >
          <Typography variant="body2" sx={{ color: '#666' }}>
            💡 <strong>Tip:</strong> Large trips with extensive route data may take
            longer to process. This is normal and ensures accurate analysis.
          </Typography>
        </Box>

      </Box>
    );
  };

  return (
    <Container
      maxWidth="xl"
      sx={{
        py: 3,
        backgroundColor: "#f5f5f5",
        minHeight: "100vh",
      }}
    >
      {loading && <LoadingStage />}

      {!loading && error && (
        <Alert severity="error" sx={{ mt: 4 }}>
          {error}
        </Alert>
      )}

      {!loading && !error && (
        <>
      {/* Header with User Profile - Compact Layout */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={9}>
          <Paper
            elevation={0}
            sx={{
              p: 3,
              backgroundColor: "white",
              borderRadius: 2,
            }}
          >
            <Typography variant="h4" fontWeight={700} sx={{ mb: 1, color: "#000" }}>
              Trip Details
            </Typography>
            <Typography variant="body2" sx={{ color: "#000" }}>
              Comprehensive trip analysis and route visualization
            </Typography>

            {/* Trip Start and End Locations */}
            {stops && stops.length > 0 && (
              <Box sx={{ mt: 2 }}>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <LocationOnIcon sx={{ color: "#4caf50", fontSize: 20 }} />
                      <Box>
                        <Typography variant="caption" sx={{ color: "#666", fontWeight: 600 }}>
                          Trip Start
                        </Typography>
                        <Typography variant="body2" sx={{ color: "#000", fontWeight: 600 }}>
                          {getAddress(stops[0].lat, stops[0].lon)}
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>
                  <Grid item xs={6}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <LocationOnIcon sx={{ color: "#f44336", fontSize: 20 }} />
                      <Box>
                        <Typography variant="caption" sx={{ color: "#666", fontWeight: 600 }}>
                          {trip_status == 3 ? "Trip End" : "Last Known Record"}
                        </Typography>
                        <Typography variant="body2" sx={{ color: "#000", fontWeight: 600 }}>
                          {getAddress(stops[stops.length - 1].lat, stops[stops.length - 1].lon)}
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>
                </Grid>
              </Box>
            )}
          </Paper>
        </Grid>

        {/* Compact User Profile Card */}
        <Grid item xs={12} md={3}>
          <Card
            sx={{
              backgroundColor: "white",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              p: 2,
            }}
          >
            <Badge
              overlap="circular"
              anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
              badgeContent=""
              sx={{
                "& .MuiBadge-badge": {
                  backgroundColor: getLiveBadge(liveStatus)
                    ? getLiveBadge(liveStatus).color
                    : "#bdbdbd",
                  width: 12,
                  height: 12,
                  minWidth: 0,
                  borderRadius: "50%",
                  border: "2px solid #fff",
                },
              }}
            >
              {driver?.image && !driverImageError ? (
                <Box
                  component="img"
                  src={`${config.apiTripsUrl}${driver.image}`}
                  alt={driver?.username || "Driver"}
                  onError={() => setDriverImageError(true)}
                  sx={{
                    width: 60,
                    height: 60,
                    borderRadius: "50%",
                    mb: 1,
                    border: "3px solid #1976d2",
                    objectFit: "cover",
                  }}
                />
              ) : (
                <Box
                  sx={{
                    width: 60,
                    height: 60,
                    borderRadius: "50%",
                    mb: 1,
                    border: "3px solid #1976d2",
                    backgroundColor: "#1976d2",
                    color: "white",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontWeight: 700,
                    fontSize: "20px",
                  }}
                >
                  {getInitials(driver?.username)}
                </Box>
              )}
            </Badge>
            <Typography
              variant="subtitle2"
              fontWeight={700}
              sx={{ color: "#000", textAlign: "center", mb: 0.25 }}
            >
              {driver?.username || "Unknown"}
            </Typography>
            <Typography
              variant="caption"
              sx={{ color: "#000", textAlign: "center", mb: 1, fontSize: "0.7rem" }}
            >
              Trip ID: {id}
            </Typography>
            <Box sx={{ mb: 1 }}>
              {getLiveBadge(liveStatus) ? (
                <Chip
                  label={getLiveBadge(liveStatus).label}
                  size="small"
                  sx={{
                    fontSize: "11px",
                    height: "22px",
                    color: getLiveBadge(liveStatus).color,
                    backgroundColor: getLiveBadge(liveStatus).bg,
                  }}
                />
              ) : (
                <Typography variant="caption" sx={{ color: "#999" }}>
                  No live status
                </Typography>
              )}
            </Box>
            <Divider sx={{ my: 1, width: "100%" }} />
            <Typography
              variant="caption"
              sx={{ color: "#000", textAlign: "center", fontWeight: 600, fontSize: "0.7rem" }}
            >
              Distance Covered
            </Typography>
            <Typography
              variant="subtitle2"
              sx={{ color: "#1976d2", fontWeight: 700, textAlign: "center" }}
            >
              {summary.distance_covered_km?.toFixed(1)} km
            </Typography>
          </Card>
        </Grid>
      </Grid>

      {/* Top 4 Metric Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {[
          {
            label: "Distance",
            value: summary.distance_covered_km?.toFixed(1) || 0,
            unit: "kilometers",
            icon: <DirectionsCarIcon sx={{ color: "#1976d2", fontSize: 32 }} />,
            bg: "#e3f2fd",
          },
          {
            label: "Avg Speed",
            value: summary.average_speed_kmh?.toFixed(1) || 0,
            unit: "km/h",
            icon: <SpeedIcon sx={{ color: "#7b1fa2", fontSize: 32 }} />,
            bg: "#f3e5f5",
          },
          {
            label: "Total Stops",
            value: summary.total_stops || 0,
            unit: "stops made",
            icon: <StopCircleIcon sx={{ color: "#f57c00", fontSize: 32 }} />,
            bg: "#fff3e0",
          },
          {
            label: "Duration",
            value: `${Math.round(drivingTimeSeconds / 3600)}h`,
            unit: "total time",
            icon: <TrendingUpIcon sx={{ color: "#388e3c", fontSize: 32 }} />,
            bg: "#e8f5e9",
          },
        ].map((stat, idx) => (
          <Grid item xs={12} sm={6} md={3} key={idx}>
            <Card sx={{ backgroundColor: "white", height: "100%" }}>
              <CardContent>
                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                  <Box
                    sx={{
                      backgroundColor: stat.bg,
                      borderRadius: 2,
                      p: 1,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    {stat.icon}
                  </Box>
                  <Box>
                    <Typography variant="body2" sx={{ color: "#000", fontWeight: 600 }}>
                      {stat.label}
                    </Typography>
                    <Typography variant="h5" fontWeight={700} sx={{ color: "#000" }}>
                      {stat.value}
                    </Typography>
                    <Typography variant="caption" sx={{ color: "#000" }}>
                      {stat.unit}
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Trip Analytics Section */}
      <Paper elevation={0} sx={{ p: 3, mb: 3, backgroundColor: "white", borderRadius: 2 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 3 }}>
          <AnalyticsIcon sx={{ color: "#1976d2" }} />
          <Typography variant="h6" fontWeight={700} sx={{ color: "#000" }}>
            Trip Analytics
          </Typography>
        </Box>

        <Grid container spacing={3}>
          {/* Speed Distribution */}
          <Grid item xs={12} md={6}>
            <Card sx={{ backgroundColor: "white" }}>
              <CardContent>
                <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2, color: "#000" }}>
                  Speed Distribution
                </Typography>
                <ResponsiveContainer width="100%" height={350}>
                  <PieChart margin={{ top: 20, right: 80, left: 20, bottom: 20 }}>
                    <Pie
                      data={speedDistribution.filter(d => d.value > 0)}
                      cx="40%"
                      cy="50%"
                      labelLine={true}
                      label={({ name, value }) => `${name}: ${value}`}
                      outerRadius={100}
                      fill="#8884d8"
                      dataKey="value"
                    >
                      {speedDistribution.filter(d => d.value > 0).map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(value) => [value, "Events"]} />
                  </PieChart>
                </ResponsiveContainer>
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, justifyContent: "center", mt: 2 }}>
                  {speedDistribution.map((item, idx) => (
                    <Box key={idx} sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                      <Box sx={{ width: 12, height: 12, backgroundColor: item.color, borderRadius: "2px" }} />
                      <Typography variant="caption" sx={{ color: "#000" }}>
                        {item.name}: {item.value}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Speed Trends */}
          <Grid item xs={12} md={6}>
            <Card sx={{ backgroundColor: "white" }}>
              <CardContent>
                <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2, color: "#000" }}>
                  Speed Trends Over Trip
                </Typography>
                <ResponsiveContainer width="100%" height={350}>
                  <LineChart data={speedTrends} margin={{ top: 5, right: 30, left: 0, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="time" />
                    <YAxis />
                    <Tooltip formatter={(value) => [value.toFixed(1), "km/h"]} />
                    <Legend />
                    <Line
                      type="monotone"
                      dataKey="avgSpeed"
                      stroke="#4CAF50"
                      strokeWidth={2}
                      name="Avg Speed"
                      dot={{ fill: "#4CAF50", r: 4 }}
                      activeDot={{ r: 6 }}
                    />
                    <Line
                      type="monotone"
                      dataKey="peakSpeed"
                      stroke="#FF7043"
                      strokeWidth={2}
                      strokeDasharray="5 5"
                      name="Peak Speed"
                      dot={{ fill: "#FF7043", r: 4 }}
                      activeDot={{ r: 6 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </Grid>

          {/* Longest Stops */}
          <Grid item xs={12} md={6}>
            <Card sx={{ backgroundColor: "white" }}>
              <CardContent>
                <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2, color: "#000" }}>
                  Longest Stops (minutes)
                </Typography>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={longestStops}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" />
                    <YAxis />
                    <Tooltip />
                    <Bar dataKey="duration" fill="#1976d2" radius={[8, 8, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </Grid>

          {/* Trip Performance Metrics */}
          <Grid item xs={12} md={6}>
            <Card sx={{ backgroundColor: "white" }}>
              <CardContent>
                <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 3, color: "#000" }}>
                  Trip Performance Metrics
                </Typography>
                <Grid container spacing={2}>
                  {[
                    {
                      label: "Driving Time",
                      value: `${Math.round(drivingTimeSeconds / 60)}m`,
                      bg: "#e8f5e9",
                      color: "#2e7d32",
                    },
                    {
                      label: "Stop Time",
                      value: `${Math.round(totalStopTime / 60)}m`,
                      bg: "#fff3e0",
                      color: "#e65100",
                    },
                    {
                      label: "Safety Score",
                      value: `${Math.round(safetyScore)}%`,
                      bg: "#e3f2fd",
                      color: "#1565c0",
                    },
                    {
                      label: "Efficiency",
                      value: `${efficiency.toFixed(1)} km/h`,
                      bg: "#f3e5f5",
                      color: "#6a1b9a",
                    },
                  ].map((metric, idx) => (
                    <Grid item xs={6} key={idx}>
                      <Box
                        sx={{
                          backgroundColor: metric.bg,
                          borderRadius: 2,
                          p: 2,
                          textAlign: "center",
                        }}
                      >
                        <Typography
                          variant="h5"
                          fontWeight={700}
                          sx={{ color: metric.color, mb: 0.5 }}
                        >
                          {metric.value}
                        </Typography>
                        <Typography variant="caption" sx={{ color: "#000" }}>
                          {metric.label}
                        </Typography>
                      </Box>
                    </Grid>
                  ))}
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Paper>

      {/* Trip Route & Timeline */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        {/* Trip Route Visualization with Interactive Markers */}
        <Grid item xs={12} md={8}>
          <Card sx={{ backgroundColor: "white", overflow: "hidden" }}>
            <CardContent sx={{ p: 0 }}>
              <Box sx={{ p: 2, backgroundColor: "#f0f0f0", borderBottom: "1px solid #ddd" }}>
                <Typography variant="subtitle2" fontWeight={600} sx={{ color: "#000", mb: 1 }}>
                  📍 Trip Route Map with Markers
                </Typography>
                <Typography variant="caption" sx={{ color: "#000" }}>
                  🟠 Stops | 🔴 High Speed Events | 🔵 Logs
                </Typography>
              </Box>

              {stops && stops.length > 0 ? (
                <Box
                  id="trip-map"
                  sx={{
                    height: 500,
                    backgroundColor: "#e0e0e0",
                    position: "relative",
                  }}
                />
              ) : (
                <Box
                  sx={{
                    height: 400,
                    backgroundColor: "#f5f5f5",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <Typography sx={{ color: "#000" }}>No route data available</Typography>
                </Box>
              )}

              {/* Trip Summary Below Map */}
              <Box sx={{ p: 2, backgroundColor: "#fafafa", borderTop: "1px solid #ddd" }}>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Box>
                      <Typography variant="caption" sx={{ color: "#666", fontWeight: 600 }}>
                        Trip Start
                      </Typography>
                      <Typography variant="body2" sx={{ color: "#000", fontWeight: 600 }}>
                        {stops[0] ? getAddress(stops[0].lat, stops[0].lon) : "N/A"}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={6}>
                    <Box>
                      <Typography variant="caption" sx={{ color: "#666", fontWeight: 600 }}>
                        {trip_status == 3 ? "Trip End" : "Last Known Record"}
                      </Typography>
                      <Typography variant="body2" sx={{ color: "#000", fontWeight: 600 }}>
                        {stops[stops?.length - 1] ? getAddress(stops[stops.length - 1].lat, stops[stops.length - 1].lon) : "N/A"}
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Trip Timeline */}
        <Grid item xs={12} md={4}>
          <Card sx={{ backgroundColor: "white", height: "100%" }}>
            <CardContent>
              <Typography variant="h6" fontWeight={700} sx={{ mb: 3, color: "#000" }}>
                Trip Timeline
              </Typography>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                <Box>
                  <Typography variant="body2" sx={{ color: "#000", fontWeight: 600 }}>
                    Trip Started
                  </Typography>
                  <Typography variant="body1" sx={{ color: "#000" }}>
                    {summary.start_time
                      ? formatDateTime(summary.start_time)
                      : "N/A"}
                  </Typography>
                  <Chip label="Start" size="small" color="success" sx={{ mt: 1 }} />
                </Box>

                <Divider />

                <Box>
                  <Typography variant="body2" sx={{ color: "#000", fontWeight: 600 }}>
                    {trip_status == 3 ? "Trip Ended" : "Last Known Record"}
                  </Typography>
                  <Typography variant="body1" sx={{ color: "#000" }}>
                    {summary.end_time
                      ? formatDateTime(summary.end_time)
                      : "N/A"}
                  </Typography>
                  {trip_status == 3 && <Chip label="End" size="small" color="error" sx={{ mt: 1 }} />}
                </Box>

                <Divider />

                <Box>
                  <Typography variant="body2" sx={{ color: "#000", fontWeight: 600 }}>
                    Total Stop Duration
                  </Typography>
                  <Typography variant="body1" sx={{ color: "#000" }}>
                    {formatDuration(totalStopTime)}
                  </Typography>
                  <Chip label="Stops" size="small" color="info" sx={{ mt: 1 }} />
                </Box>

                <Divider />

                <Box>
                  <Typography variant="body2" sx={{ color: "#000", fontWeight: 600, mb: 1 }}>
                    Day-wise Distance:
                  </Typography>
                  {daily_stats && daily_stats.length > 0 ? (
                    <TableContainer>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell
                              sx={{
                                fontWeight: 600,
                                color: "#000",
                                fontSize: "0.75rem",
                                padding: "4px 8px",
                              }}
                            >
                              Day
                            </TableCell>
                            <TableCell
                              sx={{
                                fontWeight: 600,
                                color: "#000",
                                fontSize: "0.75rem",
                                padding: "4px 8px",
                              }}
                            >
                              Date
                            </TableCell>
                            <TableCell
                              align="right"
                              sx={{
                                fontWeight: 600,
                                color: "#000",
                                fontSize: "0.75rem",
                                padding: "4px 8px",
                              }}
                            >
                              Distance
                            </TableCell>
                            <TableCell
                              align="right"
                              sx={{
                                fontWeight: 600,
                                color: "#000",
                                fontSize: "0.75rem",
                                padding: "4px 8px",
                              }}
                            >
                              Time
                            </TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {daily_stats.map((day, idx) => {
                            const dateObj = new Date(day.date);
                            const formattedDate = `${String(dateObj.getDate()).padStart(2, "0")}-${String(dateObj.getMonth() + 1).padStart(2, "0")}-${dateObj.getFullYear()}`;

                            const distanceValue = parseFloat(day.distance.replace(/[^\d.]/g, "")) || 0;
                            const distanceDisplay = distanceValue > 0 ? `${distanceValue.toFixed(2)} km` : day.distance;

                            return (
                              <TableRow key={idx} hover>
                                <TableCell
                                  sx={{
                                    color: "#000",
                                    fontSize: "0.7rem",
                                    padding: "4px 8px",
                                  }}
                                >
                                  Day {idx + 1}
                                </TableCell>
                                <TableCell
                                  sx={{
                                    color: "#000",
                                    fontSize: "0.7rem",
                                    padding: "4px 8px",
                                  }}
                                >
                                  {formattedDate}
                                </TableCell>
                                <TableCell
                                  align="right"
                                  sx={{
                                    color: "#000",
                                    fontSize: "0.7rem",
                                    padding: "4px 8px",
                                  }}
                                >
                                  {distanceDisplay}
                                </TableCell>
                                <TableCell
                                  align="right"
                                  sx={{
                                    color: "#000",
                                    fontSize: "0.7rem",
                                    padding: "4px 8px",
                                  }}
                                >
                                  {day.total_time_traveled || "0 hrs"}
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  ) : (
                    <Typography variant="caption" sx={{ color: "#000" }}>
                      No daily stats available
                    </Typography>
                  )}
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Stops & High Speed Events */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} md={6}>
          <Card sx={{ backgroundColor: "white" }}>
            <Accordion expanded={expandedAccordion === 'stops'} onChange={() => setExpandedAccordion(expandedAccordion === 'stops' ? '' : 'stops')}>
              <AccordionSummary
                expandIcon={<ExpandMoreIcon sx={{ color: "#fff" }} />}
                sx={{ backgroundColor: "#1a237e" }}
              >
                <StopCircleIcon sx={{ mr: 1, color: "#ff9800" }} />
                <Typography fontWeight={600} sx={{ color: "#fff" }}>
                  Stops Details ({stops?.length || 0} stops)
                </Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ p: 0 }}>
                <TableContainer sx={{ backgroundColor: "white" }}>
                  <Table size="small">
                    <TableHead sx={{ backgroundColor: "#fff" }}>
                      <TableRow>
                        <TableCell
                          sx={{ fontWeight: 600, color: "#000", cursor: "pointer" }}
                          onClick={() => handleStopsSort("time")}
                        >
                          Time {stopsSortBy === "time" && (stopsSortOrder === "asc" ? "↑" : "↓")}
                        </TableCell>
                        <TableCell
                          sx={{ fontWeight: 600, color: "#000", cursor: "pointer" }}
                          onClick={() => handleStopsSort("duration")}
                        >
                          Duration {stopsSortBy === "duration" && (stopsSortOrder === "asc" ? "↑" : "↓")}
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 600, color: "#000" }}>
                          Location
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {sortedStops
                        ?.slice(stopsPage * stopsRowsPerPage, (stopsPage + 1) * stopsRowsPerPage)
                        .map((stop, idx) => (
                          <TableRow
                            key={idx}
                            hover
                            onClick={() => handleStopClick(stop, stopsPage * stopsRowsPerPage + idx)}
                            sx={{
                              backgroundColor: stop.duration_sec < 300
                                ? "#fff9c4"
                                : selectedStopIdx === (stopsPage * stopsRowsPerPage + idx)
                                  ? "#e3f2fd"
                                  : "#fff",
                              cursor: "pointer",
                              "&:hover": { backgroundColor: stop.duration_sec < 300 ? "#fff59d" : "#f0f7ff" },
                            }}
                          >
                            <TableCell sx={{ color: "#000" }}>
                              {formatDateTime(stop.start_time)}
                            </TableCell>
                            <TableCell sx={{ color: "#000" }}>
                              {formatDuration(stop.duration_sec)}
                            </TableCell>
                            <TableCell align="right">
                              <Typography variant="caption" sx={{ color: "#333", maxWidth: 200, display: "block", textAlign: "right" }}>
                                {getAddress(stop.lat, stop.lon)}
                              </Typography>
                            </TableCell>
                          </TableRow>
                        ))}
                    </TableBody>
                  </Table>
                </TableContainer>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", p: 2, backgroundColor: "#fff" }}>
                  <Typography variant="caption" sx={{ color: "#000" }}>
                    Showing {Math.min(stopsPage * stopsRowsPerPage + 1, stops?.length || 0)} to {Math.min((stopsPage + 1) * stopsRowsPerPage, stops?.length || 0)} of {stops?.length || 0}
                  </Typography>
                  <Box sx={{ display: "flex", gap: 1 }}>
                    <Button
                      size="small"
                      disabled={stopsPage === 0}
                      onClick={() => setStopsPage(stopsPage - 1)}
                      sx={{ color: "#1976d2" }}
                    >
                      Previous
                    </Button>
                    <Button
                      size="small"
                      disabled={(stopsPage + 1) * stopsRowsPerPage >= (stops?.length || 0)}
                      onClick={() => setStopsPage(stopsPage + 1)}
                      sx={{ color: "#1976d2" }}
                    >
                      Next
                    </Button>
                  </Box>
                </Box>
              </AccordionDetails>
            </Accordion>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card sx={{ backgroundColor: "white", mt: 3 }}>
            <Accordion expanded={expandedAccordion === 'fastag-records'} onChange={() => setExpandedAccordion(expandedAccordion === 'fastag-records' ? '' : 'fastag-records')}>
              <AccordionSummary
                expandIcon={<ExpandMoreIcon sx={{ color: "#fff" }} />}
                sx={{ backgroundColor: "#00695c" }}
              >
                <DirectionsCarIcon sx={{ mr: 1, color: "#4db6ac" }} />
                <Typography fontWeight={600} sx={{ color: "#fff" }}>
                  Fasttag Records ({fastagData?.fastag_transactions?.length || 0})
                </Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ p: 0 }}>
                <TableContainer sx={{ backgroundColor: "white" }}>
                  <Table size="small">
                    <TableHead sx={{ backgroundColor: "#fff" }}>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 600, color: "#000" }}>
                          Read Time
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600, color: "#000" }}>
                          Toll Plaza Name
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 600, color: "#000" }}>
                          Location
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {loadingFastag ? (
                        <TableRow>
                          <TableCell colSpan={3} align="center">
                            <CircularProgress size={24} />
                          </TableCell>
                        </TableRow>
                      ) : fastagData?.fastag_transactions?.length > 0 ? (
                        fastagData.fastag_transactions
                          .slice(fastagPage * fastagRowsPerPage, (fastagPage + 1) * fastagRowsPerPage)
                          .map((transaction, idx) => {
                            const { lat, lon } = parseGeocode(transaction.toll_plaza_geocode);
                            return (
                              <TableRow
                                key={idx}
                                hover
                                sx={{
                                  backgroundColor: idx % 2 === 0 ? "#fafafa" : "#fff",
                                  cursor: "pointer",
                                  "&:hover": { backgroundColor: "#e0f2f1" },
                                }}
                              >
                                <TableCell sx={{ color: "#000" }}>
                                  {formatDateTime(transaction.reader_read_time)}
                                </TableCell>
                                <TableCell sx={{ color: "#000" }}>
                                  {transaction.toll_plaza_name}
                                </TableCell>
                                <TableCell align="right">
                                  <Typography variant="caption" sx={{ color: "#333", maxWidth: 200, display: "block", textAlign: "right" }}>
                                    {lat && lon ? getAddress(lat, lon) : transaction.toll_plaza_geocode}
                                  </Typography>
                                </TableCell>
                              </TableRow>
                            );
                          })
                      ) : (
                        <TableRow>
                          <TableCell colSpan={3} align="center">
                            <Typography variant="body2" sx={{ color: "#000" }}>
                              No fastag records available
                            </Typography>
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
                {fastagData?.fastag_transactions?.length > fastagRowsPerPage && (
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", p: 2, backgroundColor: "#fff" }}>
                    <Typography variant="caption" sx={{ color: "#000" }}>
                      Showing {Math.min(fastagPage * fastagRowsPerPage + 1, fastagData.fastag_transactions.length)} to {Math.min((fastagPage + 1) * fastagRowsPerPage, fastagData.fastag_transactions.length)} of {fastagData.fastag_transactions.length}
                    </Typography>
                    <Box sx={{ display: "flex", gap: 1 }}>
                      <Button
                        size="small"
                        disabled={fastagPage === 0}
                        onClick={() => setFastagPage(fastagPage - 1)}
                        sx={{ color: "#1976d2" }}
                      >
                        Previous
                      </Button>
                      <Button
                        size="small"
                        disabled={(fastagPage + 1) * fastagRowsPerPage >= fastagData.fastag_transactions.length}
                        onClick={() => setFastagPage(fastagPage + 1)}
                        sx={{ color: "#1976d2" }}
                      >
                        Next
                      </Button>
                    </Box>
                  </Box>
                )}
              </AccordionDetails>
            </Accordion>
          </Card>
        </Grid>
      </Grid>

      {/* Trip Insights & Recommendations */}
      <Paper elevation={0} sx={{ p: 3, mb: 3, backgroundColor: "white", borderRadius: 2 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 3 }}>
          <TrendingUpIcon sx={{ color: "#1976d2" }} />
          <Typography variant="h6" fontWeight={700} color="#000">
            Trip Insights & Recommendations
          </Typography>
        </Box>

        <Grid container spacing={2}>
          {[
            {
              title: "Driving Performance",
              bg: distanceRating === "Extreme" ? "#fff3e0" : distanceRating === "Good" ? "#e8f5e9" : "#ffebee",
              borderLeft: distanceRating === "Extreme" ? "4px solid #ff9800" : distanceRating === "Good" ? "4px solid #4caf50" : "4px solid #f44336",
              icon: <CheckCircleIcon sx={{ color: distanceRating === "Extreme" ? "#ff9800" : distanceRating === "Good" ? "#4caf50" : "#f44336" }} />,
              content: [
                `Avg Distance/Day: ${avgDistancePerDay.toFixed(1)} km`,
                `Distance Rating: ${distanceRating}`,
                `Total Distance: ${summary.distance_covered_km?.toFixed(1)} km`,
                `Average Speed: ${summary.average_speed_kmh?.toFixed(1)} km/h (${avgSpeedRating})`,
              ],
            },
            {
              title: "Safety Analysis",
              bg: safetyRating === "Excellent" ? "#e8f5e9" : safetyRating === "Good" ? "#e3f2fd" : safetyRating === "Fair" ? "#fff3e0" : "#ffebee",
              borderLeft: safetyRating === "Excellent" ? "4px solid #4caf50" : safetyRating === "Good" ? "4px solid #2196f3" : safetyRating === "Fair" ? "4px solid #ff9800" : "4px solid #f44336",
              icon: <WarningIcon sx={{ color: safetyRating === "Excellent" ? "#4caf50" : safetyRating === "Good" ? "#2196f3" : safetyRating === "Fair" ? "#ff9800" : "#f44336" }} />,
              content: [
                `Safety Score: ${Math.round(safetyScore)}% (${safetyRating})`,
                `High Speed Events: ${high_speed_events?.length || 0}`,
                `Dangerous Speeds (>110): ${summary.dangerous_speeds_count || 0}`,
                `Max Speed: ${summary.max_speed_kmh?.toFixed(1)} km/h`,
              ],
            },
            {
              title: "Stop Efficiency",
              bg: stopsRating === "Few" ? "#e8f5e9" : stopsRating === "Moderate" ? "#e3f2fd" : "#fff3e0",
              borderLeft: stopsRating === "Few" ? "4px solid #4caf50" : stopsRating === "Moderate" ? "4px solid #2196f3" : "4px solid #ff9800",
              icon: <StopCircleIcon sx={{ color: stopsRating === "Few" ? "#4caf50" : stopsRating === "Moderate" ? "#2196f3" : "#ff9800" }} />,
              content: [
                `Total Stops: ${summary.total_stops || 0} (${stopsRating})`,
                `Total Stop Time: ${formatDuration(totalStopTime)}`,
                `Avg Stop Duration: ${Math.round((totalStopTime / (summary.total_stops || 1)) / 60)} minutes`,
              ],
            },
          ].map((insight, idx) => (
            <Grid item xs={12} md={4} key={idx}>
              <Card
                sx={{
                  backgroundColor: insight.bg,
                  borderLeft: insight.borderLeft,
                  height: "100%",
                }}
              >
                <CardContent>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
                    {insight.icon}
                    <Typography variant="subtitle1" fontWeight={700} color="#000">
                      {insight.title}
                    </Typography>
                  </Box>
                  <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                    {insight.content.map((line, i) => (
                      <Typography key={i} variant="body2" color="#000">
                        {line}
                      </Typography>
                    ))}
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Paper>

      {/* Safety Summary */}
      <Paper elevation={0} sx={{ p: 3, mb: 3, backgroundColor: "white", borderRadius: 2 }}>
        <Typography variant="h6" fontWeight={700} color="#000" sx={{ mb: 3 }}>
          Safety Summary
        </Typography>
        <Grid container spacing={2}>
          {[
            {
              label: "Harsh Braking Events",
              value: summary.harsh_brakes || 0,
              color: "#f44336",
            },
            {
              label: "High Speed Events",
              value: high_speed_events?.length || 0,
              color: "#ff9800",
            },
            {
              label: "Safety Score",
              value: `${Math.round(safetyScore)}%`,
              color: "#4caf50",
            },
          ].map((safety, idx) => (
            <Grid item xs={12} sm={6} md={4} key={idx}>
              <Card sx={{ backgroundColor: "white", textAlign: "center" }}>
                <CardContent>
                  <Typography
                    variant="h4"
                    fontWeight={700}
                    sx={{ color: safety.color, mb: 1 }}
                  >
                    {safety.value}
                  </Typography>
                  <Typography variant="body2" sx={{ color: "#000" }}>
                    {safety.label}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Paper>

      {/* Trip Evidence Records */}
      <Paper elevation={0} sx={{ p: 3, backgroundColor: "white", borderRadius: 2 }}>
        <Typography variant="h6" fontWeight={700} sx={{ color: "#000", mb: 2 }}>
          Trip Evidence Logs ({trip_records?.length || 0})
        </Typography>
        <List>
          {trip_records && trip_records.length > 0 ? (
            trip_records.map((record) => (
              <ListItem
                key={record.id}
                onClick={() => setSelectedRecord(record)}
                sx={{
                  backgroundColor: "#f5f5f5",
                  mb: 1,
                  borderRadius: 1,
                  cursor: "pointer",
                  "&:hover": { backgroundColor: "#eeeeee" },
                }}
                secondaryAction={
                  record.attachments?.length > 0 && (
                    <Chip
                      label={record.attachments[0].type}
                      size="small"
                      color="primary"
                      variant="outlined"
                    />
                  )
                }
              >
                <ListItemText
                  primary={
                    <Typography sx={{ color: "#000" }}>
                      {record.text_content || "Location Broadcast"}
                    </Typography>
                  }
                  secondary={
                    <Typography sx={{ color: "#000", fontSize: "0.85rem" }}>
                      {formatDateTime(record.created_at)}
                    </Typography>
                  }
                />
              </ListItem>
            ))
          ) : (
            <Typography variant="body2" sx={{ color: "#000" }}>
              No media records for this trip
            </Typography>
          )}
        </List>
      </Paper>

      {/* Media Preview Dialog */}
      <Dialog open={!!selectedRecord} onClose={() => setSelectedRecord(null)} maxWidth="sm" fullWidth>
        <DialogTitle
          sx={{
            m: 0,
            p: 2,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          Record Details
          <IconButton onClick={() => setSelectedRecord(null)}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {selectedRecord?.attachments && selectedRecord.attachments.length > 0 ? (
            <>
              {selectedRecord.attachments.map((att, i) => (
                <Box key={i} sx={{ mb: 2, textAlign: "center" }}>
                  {att.type === "video" ? (
                    <video
                      src={att.url || `${config.apiTripsUrl}${att.file_path}`}
                      controls
                      style={{ width: "100%", borderRadius: "8px" }}
                    />
                  ) : att.type === "image" ? (
                    <img
                      src={att.url || `${config.apiTripsUrl}${att.file_path}`}
                      alt="attachment"
                      style={{ width: "100%", borderRadius: "8px" }}
                    />
                  ) : att.type === "audio" ? (
                    <Box>
                      <audio
                        src={att.url || `${config.apiTripsUrl}${att.file_path}`}
                        controls
                        style={{ width: "100%", borderRadius: "8px" }}
                        onError={(e) => {
                          console.error('Audio playback error:', e);
                          console.error('Audio src:', att.url || `${config.apiTripsUrl}${att.file_path}`);
                          console.error('File path:', att.file_path);
                          console.error('MIME type:', att.mime_type);
                        }}
                        onLoadStart={() => {
                          console.log('Audio loading:', att.url || `${config.apiTripsUrl}${att.file_path}`);
                        }}
                      />
                      <Typography variant="caption" sx={{ display: "block", mt: 1, color: "#666" }}>
                        Format: {att.mime_type || "Unknown"} | Size: {att.file_size ? `${(att.file_size / 1024).toFixed(1)} KB` : "Unknown"}
                      </Typography>
                    </Box>
                  ) : (
                    <Typography variant="body1">Unsupported file type</Typography>
                  )}
                </Box>
              ))}
            </>
          ) : (
            <Alert severity="info">No attachments for this record</Alert>
          )}
          <Typography variant="body1" sx={{ mt: 1 }}>
            {selectedRecord?.text_content || "No description provided."}
          </Typography>
          <Typography variant="caption" sx={{ display: "block", mt: 1, color: "#000" }}>
            Logged at: {formatDateTime(selectedRecord?.created_at)}
          </Typography>
        </DialogContent>
      </Dialog>
        </>
      )}
    </Container>
  );
};

export default TripDetails;